import axios from 'axios';
import Storage from 'storage';
import { GET, POST, PATCH, DELETE } from 'utils/api';

const MVR_REQUEST_URL = 'api/mvr-request/';

export type MVRRequestTransactionType = 'PLPC' | 'CLNB' | 'CLPC';

export type MVRProductType = 'irca' | 'fleet' | 'garage';

export interface MVRRequestDriver {
  name: string;
  licence_number: string;
}

export interface MVRRequestPayload {
  transaction_type: MVRRequestTransactionType;
  carrier_id: number;
  province: number;
  product_type?: MVRProductType;
  policy_number?: string;
  quote_reference_number?: string;
  consent_confirmed: boolean;
  effective_date: string;
  drivers: MVRRequestDriver[];
}

export interface MVRRequestAcceptedResponse {
  status: 'in_progress';
  mvr_order_id: number;
}

export interface MVRRequestServiceUnavailableResponse {
  status: 'service_unavailable';
  error: string;
}

export type MVRRequestSubmitResponse =
  | MVRRequestAcceptedResponse
  | MVRRequestServiceUnavailableResponse;

export type MVRDriverResultStatus =
  | 'pending'
  | 'completed'
  | 'failed'
  | 'no_match'
  | 'timeout'
  | 'cancelled';

export interface MVRDriverResult {
  name: string;
  license_number: string;
  status: MVRDriverResultStatus;
  is_duplicate: boolean;
  mvr_data: Record<string, unknown> | null;
  error_message: string | null;
  mvr_request_id: number;
  pdf_available: boolean;
}

export type MVRRequestStatusResponse =
  | { status: 'in_progress'; mvr_order_id: number; polling_interval: number }
  | { status: 'complete'; mvr_order_id: number; drivers: MVRDriverResult[] }
  | {
      status: 'error';
      mvr_order_id: number;
      error: string;
      drivers: MVRDriverResult[];
    }
  | {
      status: 'service_unavailable';
      mvr_order_id: number;
      error: string;
      drivers: MVRDriverResult[];
    };

export interface MVRRequestApiError {
  status: number;
  data: any;
}

const buildAuthHeaders = () => ({ User: Storage.get('user')?.token });

const toApiError = (error: any): MVRRequestApiError => {
  if (!error.response) {
    return { status: 0, data: { error: 'No response' } };
  }
  return { status: error.response.status, data: error.response.data };
};

export const apiSubmitMVRRequest = (
  payload: MVRRequestPayload,
): Promise<MVRRequestSubmitResponse> => POST(MVR_REQUEST_URL, payload);

export const apiGetMVRRequestStatus = (
  mvrOrderId: number,
): Promise<MVRRequestStatusResponse> => GET(`${MVR_REQUEST_URL}${mvrOrderId}/`);

export const apiDownloadMvrRequestPdf = async (
  mvrOrderId: number,
  mvrRequestId: number,
  licenseNumber: string,
): Promise<void> => {
  const endpoint = Storage.get('API_ENDPOINT');

  try {
    const response = await axios.get(
      `${endpoint}${MVR_REQUEST_URL}${mvrOrderId}/pdf/${mvrRequestId}/`,
      { headers: buildAuthHeaders(), responseType: 'blob' },
    );

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const filename = `MVR_${licenseNumber}.pdf`;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    throw toApiError(error);
  }
};

export interface MVREnabledProvincesResponse {
  provinces: number[];
}

export const apiGetMVREnabledProvinces =
  (): Promise<MVREnabledProvincesResponse> => GET('api/mvr-request/provinces/');

export interface MVRReportListItem {
  mvr_request_id: number;
  mvr_order_id: number;
  name: string;
  license_number: string;
  status: MVRDriverResultStatus;
  is_duplicate: boolean;
  error_message: string | null;
  pdf_available: boolean;
  created_at: string;
  transaction_type: MVRRequestTransactionType;
  product_type: MVRProductType | null;
  carrier: { id: number; name: string };
  requested_by: string | null;
}

export interface MVRReportListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: MVRReportListItem[];
}

export interface MVRReportListFilters {
  driver?: string;
  carrier?: string;
  requested_by?: string;
  transaction_type?: string;
  date_from?: string;
  date_to?: string;
  sort_field?: string;
  sort_direction?: string;
  view_as_email?: string;
  view_as_branch_id?: string;
}

export const apiListMvrRequestReports = (
  page = 1,
  filters?: MVRReportListFilters,
): Promise<MVRReportListResponse> => {
  const params = new URLSearchParams({ page: String(page) });
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
  }

  return GET(`api/mvr-request/requests/?${params.toString()}`);
};

export type MVRMethod = 'precisionx' | 'carrier_portal' | 'carrier_underwriter';

export type MVRValidationConfidence = 'strict' | 'soft' | '';

export interface MVRCarrierRuleValidation {
  confidence: MVRValidationConfidence;
  pattern_regex: string;
  pattern_description: string;
}

export interface MVRCarrierRule {
  carrier_id: number;
  carrier_name: string;
  province_code: string;
  transaction_type: MVRRequestTransactionType;
  product_type: MVRProductType | '';
  mvr_method: MVRMethod;
  portal_link: string;
  validation: MVRCarrierRuleValidation | null;
}

export interface MVRCarrierRulesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: MVRCarrierRule[];
}

export interface MVRCarrierRulesFilters {
  carrier?: number;
  province?: number;
  transaction_type?: MVRRequestTransactionType;
  product_type?: MVRProductType;
}

export const apiListMvrCarrierRules = (
  filters: MVRCarrierRulesFilters,
  page = 1,
): Promise<MVRCarrierRulesResponse> => {
  const params = new URLSearchParams({ page: String(page) });
  if (filters.carrier !== undefined)
    params.append('carrier', String(filters.carrier));
  if (filters.province !== undefined)
    params.append('province', String(filters.province));
  if (filters.transaction_type)
    params.append('transaction_type', filters.transaction_type);
  if (filters.product_type) params.append('product_type', filters.product_type);
  return GET(`${MVR_REQUEST_URL}carrier-rules/?${params.toString()}`);
};

// Admin-only carrier-rule management (structured format-rule builder feeds format_rule_spec).

export type MvrCharacterType = 'digits' | 'letters' | 'alphanumeric';

export type MvrLengthSpec =
  | { mode: 'exact'; value: number }
  | { mode: 'range'; min: number | null; max: number | null };

export interface MvrFormatRuleSpec {
  startsWith?: string[] | null;
  endsWith?: string[] | null;
  length?: MvrLengthSpec | null;
  characterType?: MvrCharacterType | null;
}

export interface MVRCarrierRuleOptionItem {
  value: string;
  label: string;
}

export interface MVRCarrierRuleProvinceOption {
  id: number;
  code: string;
  name: string;
}

export interface MVRCarrierRuleOptions {
  transaction_types: MVRCarrierRuleOptionItem[];
  product_types: MVRCarrierRuleOptionItem[];
  provinces: MVRCarrierRuleProvinceOption[];
  mvr_methods: MVRCarrierRuleOptionItem[];
  format_confidences: MVRCarrierRuleOptionItem[];
}

export interface MVRAdminCarrierRule {
  id: number;
  carrier_id: number;
  transaction_type: MVRRequestTransactionType;
  product_type: MVRProductType | '';
  province_code: string;
  mvr_method: MVRMethod;
  portal_link: string;
  format_confidence: MVRValidationConfidence;
  format_pattern_regex: string;
  format_pattern_description: string;
  format_rule_spec: MvrFormatRuleSpec | null;
  created_at: string;
  updated_at: string;
  updated_by_name: string | null;
}

export interface MVRAdminCarrierRuleWritePayload {
  transaction_type: MVRRequestTransactionType;
  mvr_method: MVRMethod;
  portal_link?: string;
  format_confidence?: MVRValidationConfidence;
  format_pattern_regex?: string;
  format_pattern_description?: string;
  format_rule_spec?: MvrFormatRuleSpec | null;
}

export interface MVRAdminCarrierRuleCreatePayload extends MVRAdminCarrierRuleWritePayload {
  provinces: string[];
  product_types?: MVRProductType[];
}

export interface MVRAdminCarrierRuleUpdatePayload extends Partial<MVRAdminCarrierRuleWritePayload> {
  province_code?: string;
  product_type?: MVRProductType | '';
}

export interface MVRAdminCarrierRulesListResponse {
  rules: MVRAdminCarrierRule[];
  options: MVRCarrierRuleOptions;
}

export const apiListMvrAdminCarrierRules = (
  carrierId: number,
): Promise<MVRAdminCarrierRulesListResponse> =>
  GET(`${MVR_REQUEST_URL}admin/carriers/${carrierId}/rules/`);

export const apiCreateMvrAdminCarrierRules = (
  carrierId: number,
  payload: MVRAdminCarrierRuleCreatePayload,
): Promise<MVRAdminCarrierRule[]> =>
  POST(`${MVR_REQUEST_URL}admin/carriers/${carrierId}/rules/`, payload);

export const apiUpdateMvrAdminCarrierRule = (
  carrierId: number,
  id: number,
  payload: MVRAdminCarrierRuleUpdatePayload,
): Promise<MVRAdminCarrierRule> =>
  PATCH(`${MVR_REQUEST_URL}admin/carriers/${carrierId}/rules/${id}/`, payload);

export const apiDeleteMvrAdminCarrierRule = (
  carrierId: number,
  id: number,
): Promise<void> =>
  DELETE(`${MVR_REQUEST_URL}admin/carriers/${carrierId}/rules/${id}/`);
